// API client, router and formatting helpers.
import { useEffect, useState, useCallback, useSyncExternalStore } from 'react';

export async function api(path, opts = {}) {
  const res = await fetch(path, {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    credentials: 'same-origin',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

// ---- router (history API) ----
const listeners = new Set();
const notify = () => listeners.forEach((l) => l());
window.addEventListener('popstate', notify);
export function navigate(to, { replace = false } = {}) {
  if (replace) history.replaceState(null, '', to); else history.pushState(null, '', to);
  window.scrollTo(0, 0);
  notify();
}
export function usePath() {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => location.pathname + location.search);
}
export function Link({ to, children, ...rest }) {
  return (
    <a href={to} {...rest} onClick={(e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault(); navigate(to);
    }}>{children}</a>
  );
}

// ---- data hook ----
// Keeps the previous data while a new path loads (a filtered report dims instead of flashing); an answer for a path that
// has since changed is dropped, so a slow earlier request never overwrites a newer one.
export function useApi(path, deps = []) {
  const [state, set] = useState({ data: null, error: null, loading: true });
  const load = useCallback(() => {
    if (!path) return;
    let live = true;
    api(path).then((data) => live && set({ data, error: null, loading: false })).catch((error) => live && set({ data: null, error, loading: false }));
    return () => { live = false; };
  }, [path]);
  useEffect(load, [load, ...deps]);
  return { ...state, reload: load };
}

// ---- formatting ----
export const PLATFORM_LABEL = { reddit: 'Reddit', x: 'X', tiktok: 'TikTok', instagram: 'Instagram', threads: 'Threads', linkedin: 'LinkedIn', youtube: 'YouTube', amazon: 'Amazon reviews', facebook: 'Facebook' };
export const PLATFORM_ORDER = ['tiktok', 'instagram', 'reddit', 'youtube', 'linkedin', 'amazon', 'x', 'threads', 'facebook'];
export const pColor = (p) => `var(--p-${p})`;
export const SENT = [
  { key: 'negative', label: 'Negative', color: 'var(--neg)' },
  { key: 'mixed', label: 'Mixed', color: 'var(--mix)' },
  { key: 'neutral', label: 'No opinion', color: 'var(--neu)' },
  { key: 'positive', label: 'Positive', color: 'var(--pos)' },
  // "Neutral" is a view held without feeling; "can't tell" is no reading at all, so it is hatched as well as paler.
  { key: 'unclear', label: 'Can’t tell', color: 'var(--unc)', hatch: true },
];
// What sentiment shares are made of. Neutral ("price?", "link please") and can't-tell comments give no readable view, so
// they would only dilute the shares: they are counted apart, shown but never in a share.
export const NO_VIEW = ['neutral', 'unclear'];
export const OPINION = SENT.filter((s) => !NO_VIEW.includes(s.key));
const nOf = (sent, k) => sent?.[k]?.n ?? sent?.[k] ?? 0;
/** Comments that give a view: the denominator of every sentiment share. `sent` maps a sentiment to a count or a cell. */
export const opinions = (total, sent) => total - nOf(sent, 'neutral') - nOf(sent, 'unclear');
/**
 * Whether a target has enough evidence for a share (server/analysis/aggregate.ts enough). `t` is a saved report target or
 * a dashboard one; reports made before the rule are judged by its defaults.
 */
export const enoughOf = (t) => {
  if (t.enough) return t.enough;
  const n = t.analysed?.n ?? t.analysed, views = opinions(n, t.sentiment), posts = t.concentration?.posts ?? 0;
  return { views, posts, ok: views >= 30 && posts >= 5, minViews: 30, minPosts: 5 };
};
// Matches INTENTS in server/analysis/classify.ts; anything newer falls back to its own words.
export const INTENT_LABEL = {
  praise: 'Praise', complaint: 'Complaints', question: 'Questions', recommendation: 'Recommendations', comparison: 'Comparisons',
  purchase_intent: 'Purchase intent', experience: 'Experiences', suggestion: 'Suggestions', joke_or_banter: 'Jokes and banter', other: 'Other',
};
export const intentLabel = (k) => INTENT_LABEL[k] ?? k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
let regions;
export function countryName(code) {
  if (code === 'GLOBAL') return 'Global';
  try { regions ??= new Intl.DisplayNames(['en'], { type: 'region' }); return regions.of(code) ?? code; } catch { return code; }
}
export const nf = new Intl.NumberFormat('en-US');
export const compact = (n) => (n === null || n === undefined ? '–' : new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n));
export const pct = (x) => `${Math.round(x * 100)}%`;
/** "1 comment", "87 comments". */
export const plural = (n, word) => `${nf.format(n)} ${word}${n === 1 ? '' : 's'}`;
export const usd = (x, d = 2) => `$${(x ?? 0).toFixed(d)}`;
/** Credits as clients see them: one decimal at most. 1 credit is $1 of cost, which clients are never shown. */
export const fmtCredits = (x) => String(Math.round((x ?? 0) * 10) / 10);
export function fmtDate(iso, opts = { day: 'numeric', month: 'short', year: 'numeric' }) {
  if (!iso) return 'undated';
  const d = new Date(iso.length === 19 ? iso.replace(' ', 'T') + 'Z' : iso);
  return Number.isNaN(d.getTime()) ? 'undated' : d.toLocaleDateString('en-GB', opts);
}
export function ago(iso) {
  const d = new Date(iso.replace(' ', 'T') + (iso.length === 19 ? 'Z' : ''));
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return fmtDate(iso);
}
export const RUNNING = ['queued', 'planning', 'discovering', 'collecting_comments', 'processing_context', 'checking_evidence', 'analysing'];
export const STATUS_LABEL = {
  queued: 'Queued', planning: 'Planning', discovering: 'Finding posts', collecting_comments: 'Reading comments',
  processing_context: 'Processing video context', checking_evidence: 'Checking evidence', analysing: 'Analysing',
  complete: 'Complete', partial: 'Partly collected', failed: 'Failed', cancelled: 'Cancelled',
};
export const statusClass = (s) => (RUNNING.includes(s) ? 'running' : s === 'complete' ? 'done' : s === 'cancelled' ? 'partial' : s);
export const targetsTitle = (names) => names.join(' vs ');
